import Article from "./Article";
import ArticleCollection from "./ArticleCollection";
import { ObjectId } from 'bson'
import Space, { Configurator, Source } from "../composition/Space";
import Toggles from "@domain/toggles/Toggles";
import { isComposingPendantsCategory } from "@domain/utils/utils";

const CONFIGURATOR_IDS = {
    'CREA': 1,
    'EXPRESS': 4,
    'COMPOSITOR': 5,
    'ATTRIBUTES_CONFIGURATOR': 6
}

interface ProjectionOptions {
    withPrice: boolean,
    priceRate: number,
    discounts?: {
        [subLevelId: number]: number
    },
    articleCollection: ArticleCollection
}
export interface ConfiguratorType {
    id: 4 | 5 | 6,
    description: Configurator,
    source: Source
}

const getOID = (value) => {
    let oid = value
    if (oid.$oid) {
        oid = oid.$oid
    } else if (oid.toString) {
        oid = oid.toString()
    }

    return oid
}
export default class BaseBOM {
    props;

    constructor(props) {
        this.props = props;
        if (this.props && !this.props?._id) {
            this.props._id = new ObjectId()
        } else if (this.props?._id) {
            this.props._id = new ObjectId(getOID(this.props._id))
        }
    }

    setId(id: string) {
        this.props._id = (new ObjectId(id)).toHexString()
    }

    setPreconfigId(preconfigId: string) {
        this.props.preconfigId = preconfigId
    }

    getProps() {
        return JSON.parse(JSON.stringify(this.props))
    }

    getSublevel() {
        return this.getProps().sublevel?.id
    }

    getType() {
        return this.getProps().type
    }

    getReferenceLeadtime(articleCollection: ArticleCollection, reference: string, quantity: number) {
        const article = articleCollection.getArticle(reference)
        const leadTimeFunction = article?.getProps()?.leadtime || []
        let leadtime = null
        for (const leadTimeRange of leadTimeFunction) {
            if (leadTimeRange.from <= quantity) {
                leadtime = leadTimeRange.weeks
            }
        }
        return leadtime
    }

    getAvailabilityDate(articleCollection: ArticleCollection) {
        return 0
    }

    // TODO: Consider isPersonalized value
    isValid(articleCollection: ArticleCollection) {
        const orderReferences = this.getOrderReferences(articleCollection)
        const moq = []
        let valid = true
        for (const orderRef of orderReferences) {
            if (orderRef.moq > orderRef.quantity) {
                valid = false
            }
            moq.push({
                reference: orderRef.reference,
                moq: orderRef.moq
            })
        }
        return {
            valid,
            moq
        }
    }

    getReferenceMoq(articleCollection: ArticleCollection, reference: string) {
        const article = articleCollection?.getArticle(reference)
        return article?.getProps()?.moq
    }

    getLeadtime(articleCollection: ArticleCollection) {
        const orderReferences = this.getOrderReferences(articleCollection)
        let leadtime = null
        for (const orderReference of orderReferences) {
            if (!leadtime || leadtime < orderReference.leadtime) {
                leadtime = orderReference.leadtime
            }
        }
        return leadtime
    }

    getOrderReferences(_articleCollection?: ArticleCollection) {
        return []
    }

    getModels() {
        return []
    }

    setCreationNumber(_creationNumber: number) {
        return this
    }

    // returns 'articleCodes' or 'creationCodes' that have been allocated to this BOM.
    // articleCodes are special, individual codes, that are given to certain articles.
    // creationCodes are special, individual codes, that are given to type:CREATION BOMs
    getAllocatedCodes() {
        return []
    }

    refreshCodes(_articleCollection, _allocatedCodes, counters) {
        return { BOM: this, counters }
    }

    getArticles(_articleCollection?: ArticleCollection) {
        return []
    }

    getProjection(options: ProjectionOptions) {
        // Return the product JSON with a prices key, 
        //     - If calculation method is known, it will be calculated. 
        //     - Otherwise it will return price = 0 or the previously provided value
        // TODO: Add the new configuratorType when it is created (5 = VIBIA COMPOSITOR)

        let projection = {
            ...this.getProps()
        }

        if (options.withPrice && options.priceRate && options.articleCollection) {
            const CONFIGURATOR_IDS_PROJECTION = Object.values(CONFIGURATOR_IDS).filter(id => id != CONFIGURATOR_IDS.CREA)
            if (CONFIGURATOR_IDS_PROJECTION.includes(this.props.configuratorType?.id)) {
                const { articleCollection, priceRate } = options
                const articles = this.getArticles(options.articleCollection)
                projection.prices = {
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0
                }
                for (const article of articles) {
                    let articlePrice = 0
                    try {
                        articlePrice = articleCollection.getArticlePrice(article, priceRate).getSalePrice()
                    } catch (error) { console.error('error while trying to fetch article price from aricleCollection') }
                    let discountPercent

                    if (Toggles.isEnabled('composingPendantDetails')) {
                        discountPercent = options.discounts?.[this.getSublevel()] || 0
                    } else {
                        if (Toggles.isEnabled("composingPendantCreationWithPrices")) {
                            discountPercent = options.discounts?.[this.getSublevel()] ? options.discounts[this.getSublevel()] : 0
                        } else {
                            discountPercent = options.discounts ? options.discounts[this.getSublevel()] : 0
                        }
                    }

                    const discount = (articlePrice * (discountPercent / 100))
                    const netPrice = articlePrice - discount
                    projection.prices = {
                        totalDiscount: projection.prices.totalDiscount + discount,
                        totalGross: projection.prices.totalGross + articlePrice,
                        totalNet: projection.prices.totalNet + netPrice
                    }
                }
            } else if (!projection.prices) {
                projection.prices = {
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0
                }
            }
            projection.prices = {
                ...projection.prices,
                unitGross: projection.prices.totalGross / (projection.quantity || 1),
                unitNet: projection.prices.totalNet / (projection.quantity || 1),
                unitDiscount: projection.prices.totalDiscount / (projection.quantity || 1),
            }

            if (Toggles.isEnabled('composingPendantDetails') && projection?.compositions) {
                projection.prices = {
                    ...projection.prices,
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0,
                    unitDiscount: 0,
                    unitGross: 0,
                    unitNet: 0,
                }

                for (const composition of projection.compositions) {

                    const notCompositionCP = Toggles.isEnabled("removeProductCategoryCP")
                        ? !isComposingPendantsCategory(composition.category)
                        : composition.category?.id !== 7

                    // all code below if must die if toggle work fine
                    if (notCompositionCP || Toggles.isEnabled("applyDiscountFromDynamics")) {
                        projection.prices.totalDiscount += composition.prices.unitDiscount
                        projection.prices.totalGross += composition.prices.unitGross
                        projection.prices.totalNet += composition.prices.unitNet
                        continue
                    }

                    composition.prices = {
                        totalDiscount: 0,
                        totalGross: 0,
                        totalNet: 0,
                        unitGross: 0,
                        unitNet: 0,
                        unitDiscount: 0
                    }

                    for (const article of composition.articles) {
                        const { articleCollection, priceRate } = options

                        let articleClass = articleCollection.getArticle(article.referenceCode)
                        if (Toggles.isEnabled("AvoidMVariationPrices") && article.priceReference) {
                            articleClass.props.priceReference = article.priceReference
                        }
                        if (article.attributes?.pendantHeight?.value) {
                            articleClass.setPendantHeight(article.attributes.pendantHeight.value / 100)
                        }

                        let articlePrice = 0
                        try {
                            articlePrice = articleCollection.getArticlePrice(articleClass, priceRate).getSalePrice()
                        } catch (error) { console.error('error while trying to fetch article price from aricleCollection') }

                        const discountPercent = options.discounts?.[this.getSublevel()] || 0
                        const discount = (articlePrice * (discountPercent / 100))
                        const netPrice = articlePrice - discount

                        article.prices = {
                            totalDiscount: discount,
                            totalGross: articlePrice,
                            totalNet: netPrice,
                            unitGross: articlePrice,
                            unitNet: netPrice,
                            unitDiscount: discount,
                        }

                        article.isAvailablePrice = 1;
                        article.isPersonalized = +articleClass.isPersonalized();
                        article.currency = composition.currency;

                        composition.prices = {
                            totalDiscount: composition.prices.totalDiscount + discount * article.quantity,
                            totalGross: composition.prices.totalGross + articlePrice * article.quantity,
                            totalNet: composition.prices.totalNet + netPrice * article.quantity,
                            unitGross: composition.prices.totalGross + articlePrice * article.quantity,
                            unitNet: composition.prices.totalNet + netPrice * article.quantity,
                            unitDiscount: composition.prices.totalDiscount + discount * article.quantity,
                        }
                    }

                    projection.prices = {
                        ...projection.prices,
                        totalDiscount: (composition.prices.totalDiscount + projection.prices.totalDiscount),
                        totalGross: (composition.prices.totalGross + projection.prices.totalGross),
                        totalNet: (composition.prices.totalNet + projection.prices.totalNet)
                    }
                    //till here
                }

                projection.prices.unitDiscount = projection.prices.totalDiscount
                projection.prices.unitGross = projection.prices.totalGross
                projection.prices.unitNet = projection.prices.totalNet
                projection.prices.totalDiscount *= projection.quantity
                projection.prices.totalGross *= projection.quantity
                projection.prices.totalNet *= projection.quantity
            }
        }


        return projection
    }

    static calculateCode(prefix = 'XXXXLW', allocatedCodes = [], counter = 0) {
        if (allocatedCodes?.length) {
            const code = allocatedCodes[0]
            return {
                code,
                number: parseInt(code.slice(prefix.length), 10),
                allocatedCodes: allocatedCodes.slice(1),
                counter
            }
        } else {
            let code;
            if (Toggles.isEnabled("circusCreationCodeExtraZero") && !prefix.includes("LW")) {
                code = `${prefix}${(counter + 1).toString()}`
            } else {
                code = `${prefix}${(counter + 1).toString().padStart(9, '0')}`
            }
            return {
                code,
                number: parseInt(code.slice(prefix.length), 10),
                allocatedCodes: [],
                counter: counter + 1
            }
        }
    }

    static getConfiguratorType(space: Space): ConfiguratorType {
        const source = space?.getSource() || 'PRIVATE_AREA'
        const configurator = space?.getConfigurator() || 'EXPRESS'

        let configuratorType: ConfiguratorType = {
            id: CONFIGURATOR_IDS[configurator] as 4 | 5 | 6,
            description: configurator,
            source
        }
        return configuratorType
    }

    static parseArticleProperties(article: Article) {
        const articleData = article.getProps()

        let BOM = {
            model: articleData.model,
            referenceCode: articleData.reference,
            referenceBase: articleData.referenceBase,
            attributes: articleData.attributes,
            lumensLed: articleData.lumensLed,
            watts: articleData.watts,
            sublevel: articleData.sublevel,
            familyId: articleData.family.id, // El bom quiere el family ID unicamente
            subFamily: articleData.subFamily, // Sobra el CODE (Lo dejamos de extra)
            hasAttributes: articleData.hasAttributes, // Falta installation -> !! Hablar con Alejandro para que se permita la instalación // TODO
            applicationLocation: articleData.applicationsLocations[0],
            elementOrderValue: articleData.elementOrderValue
        }

        /* Parsing values to expected */
        if (BOM.attributes?.ledColourTemp?.value) {
            BOM.attributes.ledColourTemp.value = Number.parseInt(BOM.attributes.ledColourTemp.value)
        }

        delete BOM.applicationLocation.application.shortCode
        delete BOM.applicationLocation.location.shortCode

        delete BOM.attributes?.control?.isOptional
        delete BOM.attributes?.control?.optional
        delete BOM.attributes?.finish?.isOptional
        delete BOM.attributes?.ledColourTemp?.isOptional

        delete BOM.lumensLed?.efficiency
        delete BOM.subFamily.code
        delete BOM.watts.optionalValue

        return BOM
    }
}